import axios from 'axios';
import { baseURL } from '../Constants';

interface Get5Status {
  exists: boolean;
  get5MatchId?: number;
  status: 'not_created' | 'created' | 'live' | 'finished' | 'error';
  serverInfo?: {
    id: number;
    name: string;
    ip: string;
  };
  lastError?: string;
}

interface CreateGet5MatchRequest {
  serverId: number;
  team1Get5Id: number;
  team2Get5Id: number;
  maxMaps: number;
  mapPool: string;
  mapSides?: string[];
  // BO3 Support
  mapNumber?: number;  // 0 for BO1, 1/2/3 for BO3 map number
  mapName?: string;    // Specific map name for BO3
}

interface CreateGet5MatchResult {
  success: boolean;
  get5MatchId: number;
  apiKey?: string;
  connectInfo?: {
    ip: string;
    password: string;
  };
}

interface LiveMatchData {
  match: {
    id: number;
    status: string;
    currentMap: number;
    team1Score: number;
    team2Score: number;
    team1Name: string;
    team2Name: string;
  };
  mapStats?: {
    mapName: string;
    mapNumber: number;
    team1Score: number;
    team2Score: number;
    team1Side: string;
    team2Side: string;
    roundNumber: number;
  };
  seriesScore: {
    team1: number;
    team2: number;
  };
}

interface PlayerStats {
  steamId: string;
  name: string;
  teamName: string;
  kills: number;
  deaths: number;
  assists: number;
  adr: number;
  kast: number;
  rating: number;
}

class Get5MatchService {
  private getAuthHeaders() {
    // Try admin token first, then regular token
    const adminToken = localStorage.getItem('adminToken');
    const token = adminToken || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async checkGet5Status(challongeId: string): Promise<Get5Status> {
    try {
      const response = await axios.get(
        `${baseURL}/match/${challongeId}/get5-status`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking Get5 status:', error);
      throw error;
    }
  }

  async createGet5Match(
    challongeId: string, 
    request: CreateGet5MatchRequest
  ): Promise<CreateGet5MatchResult> {
    try {
      const response = await axios.post(
        `${baseURL}/match/${challongeId}/create-get5`,
        request,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating Get5 match:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  async getLiveMatchData(challongeId: string): Promise<LiveMatchData> {
    try {
      const response = await axios.get(
        `${baseURL}/match/${challongeId}/live`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting live match data:', error);
      throw error;
    }
  }

  async getPlayerStats(challongeId: string): Promise<{ players: PlayerStats[] }> {
    try {
      const response = await axios.get(
        `${baseURL}/match/${challongeId}/live/players`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting player stats:', error);
      throw error;
    }
  }

  async getAvailableServers(challongeId: string): Promise<{ servers: any[] }> {
    try {
      const response = await axios.get(
        `${baseURL}/match/${challongeId}/available-servers`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting available servers:', error);
      throw error;
    }
  }

  async getSpectators(): Promise<{ spectators: string[]; count: number }> {
    try {
      const response = await axios.get(
        `${baseURL}/match/spectators`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting spectators:', error);
      return { spectators: [], count: 0 };
    }
  }

  // SSE endpoints for live updates
  createMatchEventSource(challongeId: string): EventSource {
    const adminToken = localStorage.getItem('adminToken');
    const token = adminToken || localStorage.getItem('token');
    return new EventSource(
      `${baseURL}/match/${challongeId}/live/stream?token=${token}`
    );
  }

  createMapStatsEventSource(challongeId: string): EventSource {
    const adminToken = localStorage.getItem('adminToken');
    const token = adminToken || localStorage.getItem('token');
    return new EventSource(
      `${baseURL}/match/${challongeId}/live/mapstats/stream?token=${token}`
    );
  }

  createPlayerStatsEventSource(challongeId: string): EventSource {
    const adminToken = localStorage.getItem('adminToken');
    const token = adminToken || localStorage.getItem('token');
    return new EventSource(
      `${baseURL}/match/${challongeId}/live/playerstats/stream?token=${token}`
    );
  }
}

export const get5MatchService = new Get5MatchService();